import { atom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';

import { collaborativeAtom } from '@/atoms/modules/collaborative';
import { sidebarSashAtom } from '@/atoms/modules/sidebar-sash';
import { isLeader } from '@/services/collaborative/leader';
import { getAppDatabaseService } from '@/services/indexeddb';
import {
  bridge,
  collaborativeDispatchAction,
  dispatch,
  replicationSchemaEntityAction,
} from '@/utils/broadcastChannel';
import { reportError, useSettleReported } from '@/utils/reportError';

export const selectedSchemaIdAtom = atom<string | null>(null);

/** Whether the sidebar shows the name field of a schema about to be added. */
export const addingSchemaAtom = atom(false);

const asyncSchemaEntityAtom = atom(async get => {
  const id = get(selectedSchemaIdAtom);
  const service = getAppDatabaseService();

  if (!service) throw new Error('Database service is not initialized');
  if (!id) throw new Error('not found selected schema id');

  const result = await service.getSchemaEntity(id);
  if (!result) throw new Error('not found schema entity');
  if (typeof result.deletedAt === 'number') {
    throw new Error('schema entity is in the trash');
  }

  return result;
});

const schemaEntityAtom = loadable(asyncSchemaEntityAtom);

const replicationSchemaEntityAtom = atom(
  null,
  async (
    get,
    set,
    {
      id,
      actions,
    }: {
      id: string;
      actions: any;
    }
  ) => {
    const service = getAppDatabaseService();
    if (!service) throw new Error('Database service is not initialized');

    service.replicationSchemaEntity(id, actions).catch(reportError);
    dispatch(
      replicationSchemaEntityAction({
        id,
        actions: actions.filter(
          (action: any) =>
            action.type !== 'editor.sharedMouseTracker' &&
            action.type !== 'editor.sharedFocusTracker'
        ),
      })
    );

    // Peers get the unfiltered stream, because shared presence is part of the
    // session even where it is noise for the cross-tab replica. Only the tab
    // holding the lock owns the connections, so the rest hand the batch over.
    if (!get(collaborativeAtom)[id]) return;

    const action = collaborativeDispatchAction({ schemaId: id, actions });
    isLeader() ? bridge.emit(action) : dispatch(action);
  }
);

const startAddingSchemaAtom = atom(null, (get, set) => {
  set(sidebarSashAtom, draft => {
    draft.open = true;
  });
  set(addingSchemaAtom, true);
});

export const useSchemaEntity = () => useAtomValue(schemaEntityAtom);
export const useStartAddingSchema = () => useSetAtom(startAddingSchemaAtom);
export const useReplicationSchemaEntity = () =>
  useSettleReported(useSetAtom(replicationSchemaEntityAtom));
